import { createRouter, stringifySearchWith } from "@tanstack/react-router";
import { RouteError, RouteNotFound } from "./components/route-states";
import { pageMemory } from "./lib/page-memory";
import { routeTree } from "./routeTree.gen";

/** A page address's search as strings, the first value of each name. */
function searchStrings(search: string) {
  const values: Record<string, string> = {};
  for (const [name, value] of new URLSearchParams(search)) values[name] ??= value;
  return values;
}

/**
 * The creator site's router. How long it keeps pages in memory depends on the visitor
 * (lib/page-memory.ts); the server writes which kind of visitor this is on <html data-viewer>
 * (routes/__root.tsx).
 */
export function getRouter() {
  const viewer =
    typeof document === "undefined" ? "anonymous" : document.documentElement.dataset.viewer;
  return createRouter({
    routeTree,
    scrollRestoration: true,
    // Search values stay the strings the site's plain links carry (?preview=1, ?role=mid). The
    // default parser reads "1" as a number, which routes then drop and redirect away from.
    parseSearch: searchStrings,
    stringifySearch: stringifySearchWith(JSON.stringify),
    defaultPreload: false,
    ...pageMemory(viewer),
    // The page stays on screen while the next one loads; only a slow load shows its pending state.
    defaultPendingMs: 1500,
    defaultErrorComponent: RouteError,
    defaultNotFoundComponent: RouteNotFound,
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof getRouter>;
  }
}
